import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { LifeBuoy } from 'lucide-react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface SupportContactData {
  supportEmail: string;
  supportPhone: string;
  supportHours: string;
  emergencyContact: string;
}

interface ISupportAndContact {
  onSave?: (data: SupportContactData) => Promise<void> | void;
}

export const SupportAndContact: React.FC<ISupportAndContact> = ({ onSave }) => {
  const initialData: SupportContactData = {
    supportEmail: 'support@acmecorp.com',
    supportPhone: '+1 (555) 123-4567',
    supportHours: 'Mon-Fri 9AM-6PM PST',
    emergencyContact: '+1 (555) 999-0000',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  return (
    <SettingsCard
      title="Support and Contact"
      description="Manage your support and contact information"
      icon={<LifeBuoy size={20} />}
      isEditing={isEditing}
      isSaving={isSaving}
      onEditToggle={handleEditToggle}
      onSave={handleSave}
    >
      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <FloatingLabelInput
              label="Support Email"
              value={formData.supportEmail}
              onChange={(e) => handleInputChange('supportEmail', e.target.value)}
            />
            <FloatingLabelInput
              label="Support Phone"
              value={formData.supportPhone}
              onChange={(e) => handleInputChange('supportPhone', e.target.value)}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <FloatingLabelInput
              label="Support Hours"
              value={formData.supportHours}
              onChange={(e) => handleInputChange('supportHours', e.target.value)}
            />
            <FloatingLabelInput
              label="Emergency Contact"
              value={formData.emergencyContact}
              onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Support Email" text={formData.supportEmail} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Support Phone" text={formData.supportPhone} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Support Hours" text={formData.supportHours} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem
              label="Emergency Contact"
              text={formData.emergencyContact}
              helpertext="This contact will be used for urgent issues outside regular support hours."
            />
          </Paper>
        </Box>
      )}
    </SettingsCard>
  );
};
